import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { youtubeEmbed, driveEmbed, instaUrl } from "../data/videos";
import useIgMedia from "../hooks/useIgMedia";
import { Loader2 } from "lucide-react";

// Shared video lightbox used by the work carousel and social grid.
export default function VideoModal({ active, onClose }) {
  const isInsta = active?.provider === "instagram";
  const isDrive = active?.provider === "drive";
  const vertical = active?.kind === "reel";

  // Only hits the network when the active reel is an IG one — hook safely no-ops otherwise
  const igMedia = useIgMedia(isInsta ? active?.instaCode : null);

  const heading = active?.category || active?.client || active?.handle || "";

  return (
    <Dialog open={!!active} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="video-modal"
        className={`z-[90] border-white/15 bg-[#060606] p-0 ${
          isInsta ? "max-w-[420px]" : vertical ? "max-w-[380px]" : "max-w-4xl"
        }`}
      >
        {active && (
          <div>
            <DialogTitle className="sr-only">
              {active.title || heading}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {active.role || heading}
            </DialogDescription>

            {isInsta ? (
              <InstaPlayer media={igMedia} poster={active.poster} />
            ) : (
              <div
                className={`w-full overflow-hidden bg-black ${
                  vertical ? "aspect-[9/16]" : "aspect-video"
                }`}
              >
                <iframe
                  title={active.title || heading}
                  src={isDrive ? driveEmbed(active.driveId) : youtubeEmbed(active?.youtubeId)}
                  className="h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}

            <div className="flex items-center justify-between gap-4 px-5 py-4">
              <div>
                <div className="font-hud text-[10px] uppercase tracking-[0.2em] text-[#CBFF3A]">
                  {heading}
                </div>
                <h3 className="mt-1 font-display text-lg font-600 tracking-tight text-white">
                  {active.title || active.role}
                </h3>
                {(active.note || (active.role && active.title)) && (
                  <p className="mt-1 font-body text-xs text-white/45">
                    {active.note || active.role}
                  </p>
                )}
              </div>
              {isInsta ? (
                <a
                  href={instaUrl(active.instaCode)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="modal-insta-link"
                  className="shrink-0 rounded-full border border-white/20 px-4 py-2 font-hud text-[10px] uppercase tracking-widest text-white/70 transition-colors hover:border-[#CBFF3A] hover:text-[#CBFF3A]"
                >
                  View on Instagram
                </a>
              ) : (
                active.year && (
                  <div className="shrink-0 font-hud text-[11px] uppercase tracking-widest text-white/40">
                    {active.duration} · {active.year}
                  </div>
                )
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function InstaPlayer({ media, poster }) {
  return (
    <div className="relative aspect-[9/16] w-full overflow-hidden bg-black">
      {media.status === "loading" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/60">
          <Loader2 className="h-6 w-6 animate-spin text-[#CBFF3A]" />
          <span className="font-hud text-[10px] uppercase tracking-[0.2em]">
            Loading reel…
          </span>
        </div>
      )}

      {media.status === "ok" && media.video && (
        <video
          key={media.video}
          data-testid="insta-native-video"
          src={media.video}
          poster={media.thumb || undefined}
          controls
          autoPlay
          muted
          playsInline
          preload="auto"
          className="h-full w-full bg-black object-contain"
        />
      )}

      {media.status === "fail" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-8 text-center">
          {poster && (
            <img
              src={poster}
              alt=""
              className="absolute inset-0 h-full w-full object-cover opacity-20"
            />
          )}
          <div className="relative z-10">
            <p className="font-hud text-[10px] uppercase tracking-[0.2em] text-white/40">
              Reel unavailable inline
            </p>
            <p className="mt-2 font-display text-sm text-white/70">
              Use the "View on Instagram" button below to watch this reel.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
